import React from "react";
import { motion } from "framer-motion";
import SEOComponent from "./components/SEOComponent";
import OptimizedImage from "./components/OptimizedImage";
import {
  FaBullseye,
  FaRocket,
  FaGlobe,
  FaShieldAlt,
  FaHandshake,
  FaUsers,
  FaChartLine,
  FaAward,
} from "react-icons/fa";
import heroImage from "./assets/industries1.png";
import aboutImage from "./assets/whoweare.png"
import apart from "./assets/Apart.webp";

const About = () => {
  const stats = [
    { number: "200+", label: "Projects Delivered", icon: <FaChartLine /> },
    { number: "50+", label: "Enterprise Partners", icon: <FaUsers /> },
    { number: "20+", label: "Years Experience", icon: <FaAward /> },
    { number: "99.99%", label: "Client Satisfaction", icon: <FaBullseye /> },
  ];

  const whoWeArePoints = [
    {
      title: "AI Consulting & Business Transformation",
      points: [
        "Leading AI strategy and implementation partner",
        "Driving growth with data intelligence",
        "Trusted advisor for automation and analytics",
      ],
    },
    {
      title: "Digital & Social Media Marketing",
      points: [
        "Data-driven campaigns that convert",
        "Creative storytelling across every platform",
        "Measurable growth through targeted marketing",
      ],
    },
    {
      title: "Event Management & Production",
      points: [
        "Seamless planning to flawless execution",
        "Immersive experiences that captivate audiences",
        "High-impact events, end-to-end delivery",
      ],
    },
  ];

  const whatsetsapart = [
    "At Alif, we Combine advanced technology, AI-driven solutions, and IT services aligned with clients’ vision and goals.",
    "Go beyond traditional strategies by integrating innovation with human insight.",
    "Deliver expertise across IT Transformation, digital marketing, social media, and event management.",
    "Focus on measurable results, meaningful experiences, and sustainable business growth.",
  ];

  const coreValues = [
    {
      icon: <FaBullseye />,
      title: "Business Outcome-First",
      description: "Business outcome-first thinking",
    },
    {
      icon: <FaRocket />,
      title: "Agile Delivery",
      description: "Agile delivery models",
    },
    {
      icon: <FaGlobe />,
      title: "Global Innovation",
      description: "Global innovation, local execution",
    },
    {
      icon: <FaShieldAlt />,
      title: "Ethical AI",
      description: "Ethical frameworks for AI and tech",
    },
    {
      icon: <FaHandshake />,
      title: "Partnership",
      description: "True transformation partnership",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-slate-900 via-gray-900 to-black text-white overflow-hidden">
      <SEOComponent
        title="About AlifShams - Leading AI Consulting & Technology Solutions Company"
        description="Learn about AlifShams, a leading technology solutions provider specializing in AI consulting, digital marketing, and business transformation. 200+ projects delivered with 99.99% client satisfaction."
        keywords="about AlifShams, technology solutions provider, AI consulting company, business transformation, digital marketing agency, enterprise solutions, innovation company"
        url="https://digitalif.net/about"
        type="website"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="About Us"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/80"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          <motion.h1
            className="text-7xl md:text-8xl font-bold mb-8 bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            ALIF
          </motion.h1>
          <motion.p
            className="text-2xl md:text-3xl mb-12 text-gray-300 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Transforming Enterprise Architecture Through Innovation
          </motion.p>

          {/* Stats Grid */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl text-blue-400 mb-2 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="mb-4 flex items-center px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center">
            <span className="text-blue-400 text-sm font-semibold tracking-wider uppercase">
              Enterprise Excellence
            </span>
            <h2 className="text-5xl font-bold mt-4 mb-6">Who We Are</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-0 min-h-[70vh] rounded-3xl overflow-hidden bg-gradient-to-r from-gray-900/50 to-slate-900/50 backdrop-blur-sm">
            {/* Left Side - Image */}
            <motion.div
              className="flex flex-col space-y-6 p-8"
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-center">
                <p className="text-xl font-semibold text-gray-300 leading-relaxed">
                  We redefine business success through a unified ecosystem of
                  advanced technology, AI-driven solutions, strategic marketing,
                  and immersive event experiences.
                </p>
              </div>
              <OptimizedImage
                src={aboutImage}
                alt="Who We Are"
                className="w-full h-auto max-h-96 object-contain rounded-2xl shadow-2xl"
              />
              {/* <div className="absolute bottom-8 left-8 right-8">
                <div className="bg-black/80 backdrop-blur-md rounded-2xl p-6">
                  <h3 className="text-2xl font-bold text-white mb-3">Transforming Tomorrow</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We redefine business success through a unified ecosystem of advanced technology, AI-driven solutions, strategic marketing, and immersive event experiences.
                  </p>
                </div>
              </div> */}
            </motion.div>

            {/* Right Side - Content */}
            <motion.div
              className="px-8 lg:px-12 flex flex-col justify-center space-y-8"
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {whoWeArePoints.map((service, index) => (
                <motion.div
                  key={index}
                  className="group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex items-start space-x-4 p-6 rounded-xl hover:bg-white/5 transition-all duration-300">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                        {service.title}
                      </h4>
                      <ul className="space-y-2">
                        {service.points.map((point, pointIndex) => (
                          <li
                            key={pointIndex}
                            className="flex items-start text-gray-300 text-sm"
                          >
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            <span className="leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className="py-12 px-4 bg-gradient-to-r from-gray-900/50 to-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            <div
              className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 p-12 rounded-3xl border border-blue-500/30"
              data-aos="fade-right"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mb-8">
                <FaBullseye className="text-2xl text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                Our mission is to empower businesses by combining AI-driven
                solutions, expert consulting, IT services, and integrated
                digital and social marketing with innovative event management.
                We deliver seamless technology integration and intelligent
                strategies that foster growth, efficiency, and transformation in
                the digital era.
              </p>
            </div>

            <div
              className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 p-12 rounded-3xl border border-purple-500/30"
              data-aos="fade-left"
            >
              <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mb-8">
                <FaRocket className="text-2xl text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-6">Our Vision</h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                We aim to redefine business success through a unified ecosystem
                that merges advanced technology, AI-driven solutions, strategic
                marketing, and impactful event experiences. Our approach blends
                innovation, data intelligence, and creativity to deliver
                scalable IT services, measurable marketing outcomes, and
                transformative event solutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold tracking-wider uppercase">
              Competitive Advantage
            </span>
            <h2 className="text-5xl font-bold mt-4">What Sets Us Apart</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1" data-aos="fade-right">
              <OptimizedImage
                src={apart}
                alt="What sets us apart"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
            </div>

            <div className="space-y-6 order-1 lg:order-2" data-aos="fade-left">
              {whatsetsapart.map((point, index) => (
                <div
                  key={index}
                  className="flex items-start p-3 bg-gradient-to-r from-gray-800/50 to-slate-800/50 rounded-xl border-l-4 border-cyan-400"
                >
                  <div className="w-8 h-8 bg-cyan-400 rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                    <span className="text-black font-bold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-gray-300 text-lg">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 px-4 bg-gradient-to-b from-black/50 to-gray-900/50">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-emerald-400 text-sm font-semibold tracking-wider uppercase">
            Foundation
          </span>
          <h2 className="text-5xl font-bold mt-4 mb-6">Our Core Values</h2>
          <p className="text-xl text-gray-300 mb-16 max-w-3xl mx-auto">
            We work as your transformation partner—not a vendor—by combining
            these fundamental principles
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-800/80 to-slate-800/80 p-8 rounded-2xl border border-gray-700 hover:border-emerald-400/50 hover:shadow-2xl transition-all duration-50 cursor-pointer"
                style={{ transform: "translateY(0px)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-10px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0px)")
                }
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="text-4xl text-emerald-400 mb-6 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold mb-4 text-white">
                  {value.title}
                </h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
